import { Box, Typography } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { PlannerAllocation, UtilisationGranularity } from '@timelog/pm-shared';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CELL_WIDTH_BY_SCALE,
  LEFT_PANE_WIDTH_PX,
  NAME_COL_PX,
  NUM_COL_PX,
  PLANNER_HEADER_HEIGHT_PX,
  PLANNER_ROW_HEIGHT_PX,
  UTIL_COL_PX,
} from './plannerGeometry';
import { PlannerHeader } from './PlannerHeader';
import { PlannerRowView } from './PlannerRow';
import type { PlannerMetric } from './PlannerToolbar';
import type { PlannerT } from './plannerWindow';
import type { PlannerRow as PlannerRowModel, PlannerTab } from './resourcePlannerModel';

import { MicroLabel } from '@/components/ui';
import { zIndex } from '@/theme';

export type PlannerGridProps = {
  rows: PlannerRowModel[];
  buckets: readonly string[];
  granularity: UtilisationGranularity;
  metric: PlannerMetric;
  tab: PlannerTab;
  /** X inside the track, or null when today is outside the window. */
  todayX: number | null;
  scrollElement: HTMLElement | null;
  onToggle: (row: PlannerRowModel) => void;
  onMoveHours: (allocation: PlannerAllocation, anchor: HTMLElement) => void;
};

const columnTitles = (tab: PlannerTab, t: PlannerT): [string, string, string] =>
  tab === 'people'
    ? [t('grid.ColumnResource'), t('grid.ColumnAllocated'), t('grid.ColumnRegistered')]
    : [t('grid.ColumnCompetency'), t('grid.ColumnDemand'), t('grid.ColumnCapacity')];

/**
 * The utilisation grid: a sticky period band, then virtualized rows inside a
 * horizontal-only scroller. Same scroll model as `PlanTimeline` — vertical
 * scrolling belongs to `PanelContent`, the band lives OUTSIDE the horizontal
 * scroller (sticky resolves against the nearest scroll box) and mirrors its
 * offset imperatively, and the frozen left pane is `position: sticky; left: 0`
 * inside each row.
 */
export const PlannerGrid = ({
  rows,
  buckets,
  granularity,
  metric,
  tab,
  todayX,
  scrollElement,
  onToggle,
  onMoveHours,
}: PlannerGridProps) => {
  const { t } = useTranslation(['pmResources', 'pmCommon']);
  const listRef = useRef<HTMLDivElement | null>(null);
  const bodyScrollRef = useRef<HTMLDivElement | null>(null);
  const bandTrackRef = useRef<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => PLANNER_ROW_HEIGHT_PX,
    overscan: 12,
    getItemKey: (index) => rows[index]!.key,
    // Content sits above the list (header, toolbar, band); without the offset
    // the virtualizer renders the wrong slice once the page scrolls.
    scrollMargin: listRef.current?.offsetTop ?? 0,
  });

  const onBodyScroll = useCallback(() => {
    const track = bandTrackRef.current;
    const scroller = bodyScrollRef.current;
    if (track && scroller) track.style.transform = `translateX(${String(-scroller.scrollLeft)}px)`;
  }, []);

  const cellWidth = CELL_WIDTH_BY_SCALE[granularity];
  const trackWidth = buckets.length * cellWidth;
  const totalWidth = LEFT_PANE_WIDTH_PX + trackWidth;
  const [nameTitle, col1Title, col2Title] = columnTitles(tab, t);

  return (
    <Box data-automation-id="planner-grid">
      {/* Period band: sticky to the PAGE scroller, so outside the horizontal one. */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: zIndex.stickyHeader,
          overflow: 'hidden',
          height: PLANNER_HEADER_HEIGHT_PX,
          bgcolor: 'var(--bg-panel)',
          borderBottom: 'var(--border-width-thin) solid var(--border-subtle)',
        }}
      >
        <Box ref={bandTrackRef} sx={{ display: 'flex', width: `${String(totalWidth)}px` }}>
          {/* Plain flex, not Stack — MuiStack's default gap would break the 500px pane. */}
          <Box
            sx={{
              display: 'flex',
              position: 'sticky',
              left: 0,
              width: LEFT_PANE_WIDTH_PX,
              flex: 'none',
              bgcolor: 'var(--bg-panel)',
              zIndex: zIndex.rowAffordance,
              borderRight: 'var(--border-width-thin) solid var(--border-divider)',
              alignItems: 'stretch',
            }}
          >
            <Box
              sx={{
                width: NAME_COL_PX,
                flex: 'none',
                display: 'flex',
                alignItems: 'center',
                pl: '20px',
              }}
            >
              <MicroLabel>{nameTitle}</MicroLabel>
            </Box>
            {[
              { width: UTIL_COL_PX, title: t('grid.ColumnUtilisation') },
              { width: NUM_COL_PX, title: col1Title },
              { width: NUM_COL_PX, title: col2Title },
            ].map((column) => (
              <Box
                key={column.title}
                sx={{
                  width: column.width,
                  flex: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  pr: 1.5,
                  borderLeft: 'var(--border-width-thin) solid var(--border-subtle)',
                }}
              >
                {/* label, not MicroLabel: uppercase micro tracking overflows 84px. */}
                <Typography variant="label" sx={{ color: 'var(--fg-3)', whiteSpace: 'nowrap' }}>
                  {column.title}
                </Typography>
              </Box>
            ))}
          </Box>
          <PlannerHeader buckets={buckets} granularity={granularity} />
        </Box>
      </Box>

      <Box
        ref={bodyScrollRef}
        onScroll={onBodyScroll}
        sx={{ overflowX: 'auto', overflowY: 'hidden', overscrollBehaviorX: 'contain' }}
      >
        <Box
          ref={listRef}
          sx={{
            position: 'relative',
            height: `${String(virtualizer.getTotalSize())}px`,
            width: `${String(totalWidth)}px`,
            // One gradient paint for the whole period grid.
            backgroundImage: `repeating-linear-gradient(to right, var(--border-subtle) 0 1px, transparent 1px ${String(cellWidth)}px)`,
            backgroundPosition: `${String(LEFT_PANE_WIDTH_PX)}px 0`,
          }}
        >
          {todayX !== null && (
            <Box
              data-automation-id="planner-today"
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                width: '2px',
                left: `${String(LEFT_PANE_WIDTH_PX + todayX)}px`,
                bgcolor: 'var(--color-pink-80)',
                opacity: 0.6,
                pointerEvents: 'none',
                zIndex: zIndex.rowAffordance,
              }}
            />
          )}

          {virtualizer.getVirtualItems().map((item) => (
            <Box
              key={item.key}
              data-index={item.index}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${String(item.start - virtualizer.options.scrollMargin)}px)`,
              }}
            >
              <PlannerRowView
                row={rows[item.index]!}
                cellWidth={cellWidth}
                metric={metric}
                onToggle={onToggle}
                onMoveHours={onMoveHours}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
